"use client";

import { useEffect, useMemo, useState, useCallback } from "react";
import type { CompanyFilterItem, CompanyFiltersParams } from "@artha/utils";
import { SettingsBottomSheet } from "./settings-bottom-sheet";
import { toast } from "sonner";
import { PRESENTATION_MODES } from "../../constants";
import type {
  FilterOption,
  SalaryRangeInfo,
  FilterOptions,
  SettingsState,
  SettingsBottomSheetLabels,
} from "./settings-bottom-sheet";

type CompanyFiltersQueryOptions = { enabled?: boolean };
type CompanyFiltersQueryResult = {
  data?: { items: CompanyFilterItem[] };
};

export interface EnhancedSettingsBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  profile?: any; // CreatorProfileForMyLink type
  updateSettingsMutation?: any; // Mutation hook
  labels: SettingsBottomSheetLabels;
  // Job feed client functions - these need to be passed in
  useFiltersQuery: (params: any) => { data: any };
  useCompanyFiltersQuery: (
    params: CompanyFiltersParams,
    options?: CompanyFiltersQueryOptions,
  ) => CompanyFiltersQueryResult;
  useOptimizedJobCount?: (params: any, options?: any) => { data?: { count: number }; isLoading: boolean; debouncedRefetch: () => void };
  buildJobIndexParams: (params: any) => any;
  // Normalization functions
  normalizeCountryCode: (code: string) => string;
  normalizeWorkMode: (mode: string) => string;
  normalizeJobType: (type: string) => string;
  // Label functions
  getCountryLabel: (code: string) => string;
  getWorkModeLabel: (mode: string) => string;
  getJobTypeLabel: (type: string) => string;
  getNicheLabel: (niche: string) => string;
  // Analytics callbacks (optional)
  onNicheAdd?: (niches: string[], creator?: any) => void;
  onNicheRemove?: (niches: string[], creator?: any) => void;
  onKeywordAdd?: (keywords: string[], creator?: any) => void;
  onKeywordRemove?: (keywords: string[], creator?: any) => void;
  onCountryAdd?: (country: string, creator?: any) => void;
  onCountryRemove?: (country: string, creator?: any) => void;
  onIndustryAdd?: (industry: string, creator?: any) => void;
  onIndustryRemove?: (industry: string, creator?: any) => void;
  onFilterJobsApply?: (filtersChanged: string[], count: number, creator?: any) => void;
  onFilterCompanyApply?: (filtersChanged: string[], count: number, creator?: any) => void;
  presentation?: "bottom-sheet" | "drawer" | "inline";
  drawerClassName?: string;
  sheetClassName?: string;
  sheetOverlayClassName?: string;
  hideHeader?: boolean;
}

/**
 * Optimized SettingsBottomSheet with improved job count fetching performance:
 * - Debounced job count requests (500ms delay)
 * - Smart caching (2 minutes stale time, 10 minutes cache time)
 * - Request deduplication
 * - Background refetching disabled for better UX
 */
export function EnhancedSettingsBottomSheet({
  isOpen,
  onClose,
  profile,
  updateSettingsMutation,
  labels,
  useFiltersQuery,
  useCompanyFiltersQuery,
  useOptimizedJobCount,
  buildJobIndexParams,
  normalizeCountryCode,
  normalizeWorkMode,
  normalizeJobType,
  getCountryLabel,
  getWorkModeLabel,
  getJobTypeLabel,
  getNicheLabel,
  onNicheAdd,
  onNicheRemove,
  onKeywordAdd,
  onKeywordRemove,
  onCountryAdd,
  onCountryRemove,
  onIndustryAdd,
  onIndustryRemove,
  onFilterJobsApply,
  onFilterCompanyApply,
  presentation,
  drawerClassName,
  sheetClassName,
  sheetOverlayClassName,
  hideHeader,
}: EnhancedSettingsBottomSheetProps) {
  const shouldShowWebSaveToast =
    presentation === PRESENTATION_MODES.DRAWER || presentation === PRESENTATION_MODES.INLINE;

  // Current filter state for job count calculation
  const [currentFilters, setCurrentFilters] = useState<SettingsState | null>(null);
  const [companySearchQuery, setCompanySearchQuery] = useState("");

  // Prepare initial settings from profile
  const initialSettings = useMemo<Partial<SettingsState>>(() => {
    if (!profile) return {};
    const prefs = profile.settings?.jobFeedPreferences || {};
    return {
      activeTab: "jobs" as const,
      minSalary: prefs.minSalary,
      currencies: prefs.currencies || (prefs.currency ? [prefs.currency] : ["usd"]), // Handle backward compatibility
      countries: prefs.countries || [],
      // Use categories from jobFeedPreferences first, then fall back to nicheCategories
      niches: prefs.categories || profile.settings?.nicheCategories || [],
      positiveKeywords: prefs.positiveKeywords || [],
      negativeKeywords: prefs.negativeKeywords || [],
      workModes: prefs.workModes || [],
      jobTypes: prefs.jobTypes || [],
      educationLevels: prefs.educationLevels || (prefs.educationLevel ? [prefs.educationLevel] : []), // Handle backward compatibility
      experienceLevels: prefs.experienceLevels || [],
      // Employer tab
      companies: prefs.companies || [],
      industries: prefs.industries || [],
    };
  }, [profile]);

  const effectiveFilters = currentFilters ?? (initialSettings as SettingsState);

  /**
   * Normalize and map active country selections to ISO country code string (e.g., "IN" or "IN,US").
   * Passed to useFiltersQuery so the backend returns employer/company options specific to the selected location,
   * avoiding fallback to US-only employer listings when non-US countries (e.g. India) are selected.
   */
  const countries = effectiveFilters?.countries;

  const locationParam = useMemo(() => {
    if (!countries || countries.length === 0) {
      return undefined;
    }
    const normalize = normalizeCountryCode || ((code: string) => code.toUpperCase());
    return countries.map(normalize).filter(Boolean).join(",");
  }, [countries, normalizeCountryCode]);

  const activeLocation = locationParam;

  // Fetch filter options across all countries and job sources/types.
  // Deliberately do not pass location, niches, keywords, or job types here so that
  // individual country counts and company options remain complete for multi-country selection.
  const { data: filtersData } = useFiltersQuery({
    include_companies: true,
  });

  const { data: companySearchData } = useCompanyFiltersQuery(
    {
      page: 1,
      size: 100,
      search: companySearchQuery.trim() || undefined,
      location: activeLocation,
      only_enriched: false,
      only_cpa: false,
    },
    { enabled: companySearchQuery.trim().length >= 2 },
  );

  // Build job count parameters from current filters
  const jobCountParams = useMemo(() => {
    if (!effectiveFilters) {
      return null;
    }

    return buildJobIndexParams({
      creator: profile,
      params: {
        categories: effectiveFilters.niches?.length ? effectiveFilters.niches : undefined,
        location: effectiveFilters.countries,
        job_type: effectiveFilters.jobTypes,
        work_mode: effectiveFilters.workModes,
        salary_min: effectiveFilters.minSalary ?? undefined,
        education: effectiveFilters.educationLevels?.[0] || undefined,
        niche_keywords: effectiveFilters.positiveKeywords,
        industry: effectiveFilters.industries?.[0] || undefined,
        company: effectiveFilters.companies?.[0] || undefined,
      },
    });
  }, [effectiveFilters, profile, buildJobIndexParams]);

  // Optimized job count query with debouncing and caching (optional)
  const jobCountResult = useOptimizedJobCount ? useOptimizedJobCount(
    jobCountParams || {},
    {
      enabled: !!jobCountParams && !!effectiveFilters,
      debounceMs: 500, // 500ms debounce
      staleTime: 2 * 60 * 1000, // 2 minutes stale time
      cacheTime: 10 * 60 * 1000, // 10 minutes cache time
      refetchOnWindowFocus: false, // Don't refetch on window focus for better UX
    }
  ) : { data: undefined, isLoading: false, debouncedRefetch: () => { } };

  const {
    data: jobCountData,
    isLoading: isLoadingJobCount,
    debouncedRefetch,
  } = jobCountResult;

  // Transform API response to FilterOptions format
  const availableOptions = useMemo<FilterOptions | undefined>(() => {
    if (!filtersData) return undefined;

    const transformOptions = (
      options: any[] | undefined,
      labelFn?: (v: string) => string
    ): FilterOption[] =>
      (options || []).map((opt: any) => ({
        label: labelFn ? labelFn(opt.value) : opt.label || opt.value,
        value: opt.value,
        count: opt.count || 0,
      }));

    // Transform salary_ranges from API to salaryRange for UI
    // Handle invalid min_salary (-1) from API
    const salaryRange: SalaryRangeInfo | undefined = filtersData.salary_ranges
      ? {
        min: Math.max(0, filtersData.salary_ranges.min_salary || 0), // Ensure min is not negative
        max: filtersData.salary_ranges.max_salary || 200000,
        currency: filtersData.salary_ranges.currency || "USD",
        jobsWithSalary: filtersData.salary_ranges.jobs_with_salary || 0,
      }
      : {
        min: 0,
        max: 200000,
        currency: "USD",
        jobsWithSalary: 0,
      };

    // Always show work modes and job types from API if they exist, even with 0 counts
    // This allows users to select them and see "no jobs" feedback
    const workModes = filtersData.work_modes?.length > 0
      ? filtersData.work_modes
      : [
        { value: "remote", count: 0 },
        { value: "hybrid", count: 0 },
        { value: "onsite", count: 0 },
      ];

    const jobTypes = filtersData.job_types?.length > 0
      ? filtersData.job_types
      : [
        { value: "full-time", count: 0 },
        { value: "part-time", count: 0 },
        { value: "contract", count: 0 },
        { value: "internship", count: 0 },
      ];

    // For education levels, use API data or provide fallbacks
    const educationLevels = filtersData.education_levels?.length > 0
      ? filtersData.education_levels
      : [
        { value: "high_school", count: 0 },
        { value: "bachelors", count: 0 },
        { value: "masters", count: 0 },
        { value: "phd", count: 0 },
      ];

    // For experience levels, use API data if available
    const experienceLevels = filtersData.experience_levels?.length > 0
      ? filtersData.experience_levels
      : [];

    // Handle countries - merge API countries with common countries so searching/selecting any country is always available
    const defaultCountries = [
      { value: "US", count: 0 },
      { value: "CA", count: 0 },
      { value: "GB", count: 0 },
      { value: "IN", count: 0 },
      { value: "DE", count: 0 },
      { value: "AU", count: 0 },
      { value: "FR", count: 0 },
    ];
    const rawApiCountries = filtersData.countries || [];
    const seenCountryKeys = new Set<string>();
    const uniqueApiCountries: typeof rawApiCountries = [];

    for (const country of rawApiCountries) {
      const key = (country.value || "").toUpperCase();
      if (key && !seenCountryKeys.has(key)) {
        seenCountryKeys.add(key);
        uniqueApiCountries.push(country);
      }
    }

    const countries = [
      ...uniqueApiCountries,
      ...defaultCountries.filter((c) => !seenCountryKeys.has(c.value.toUpperCase())),
    ];

    console.log("[Optimized Settings] API Response Analysis:", {
      totalJobs: filtersData.total_jobs,
      workModes: {
        apiCount: filtersData.work_modes?.length || 0,
        finalCount: workModes.length,
        data: workModes
      },
      jobTypes: {
        apiCount: filtersData.job_types?.length || 0,
        finalCount: jobTypes.length,
        data: jobTypes
      },
      educationLevels: {
        apiCount: filtersData.education_levels?.length || 0,
        finalCount: educationLevels.length,
        data: educationLevels
      },
      experienceLevels: {
        apiCount: filtersData.experience_levels?.length || 0,
        finalCount: experienceLevels.length,
        data: experienceLevels
      },
      countries: {
        apiCount: filtersData.countries?.length || 0,
        finalCount: countries.length,
        data: countries
      },
      salaryRange
    });

    return {
      niches: transformOptions(filtersData.categories, getNicheLabel),
      countries: transformOptions(countries, getCountryLabel),
      workModes: transformOptions(workModes, getWorkModeLabel),
      jobTypes: transformOptions(jobTypes, getJobTypeLabel),
      educationLevels: transformOptions(educationLevels),
      experienceLevels: transformOptions(experienceLevels),
      currencies: [
        { label: "USD ($)", value: "usd" },
        { label: "INR (₹)", value: "inr" },
        { label: "EUR (€)", value: "eur" },
        { label: "GBP (£)", value: "gbp" },
      ],
      salaryRange,
      // Employer tab options
      companies: transformOptions([
        ...(filtersData.companies || []),
        ...(companySearchData?.items || []).filter(
          (candidate: CompanyFilterItem) =>
            !(filtersData.companies || []).some(
              (company: CompanyFilterItem) => company.value === candidate.value,
            ),
        ),
      ]),
      industries: transformOptions(filtersData.industries),
    };
  }, [companySearchData, filtersData, getNicheLabel, getCountryLabel, getWorkModeLabel, getJobTypeLabel]);

  // Optimized filters change handler with debounced job count fetching
  const handleFiltersChange = useCallback((state: SettingsState) => {
    console.log("[Optimized Settings] Filters changed:", state);

    // Update current filters state
    setCurrentFilters(state);

    // The debounced refetch will automatically trigger after 500ms
    debouncedRefetch();
  }, [debouncedRefetch]);

  // Set initial filters when settings open
  useEffect(() => {
    if (isOpen) {
      setCurrentFilters(initialSettings as SettingsState);
    }
  }, [isOpen, initialSettings]);

  // Handle save with normalization and analytics tracking
  const handleSaveSettings = useCallback((state: SettingsState) => {
    const prefs = profile?.settings?.jobFeedPreferences || {};
    const initialNiches = prefs.categories || profile?.settings?.nicheCategories || [];
    const initialKeywords = prefs.positiveKeywords || [];
    const initialCountries = prefs.countries || [];
    const initialIndustries = prefs.industries || [];

    const filtersAppliedCount =
      state.niches.length +
      state.positiveKeywords.length +
      state.workModes.length +
      state.jobTypes.length +
      state.educationLevels.length +
      state.experienceLevels.length +
      state.countries.length +
      state.companies.length +
      state.industries.length +
      (state.minSalary ? 1 : 0);

    // Track niche changes
    const addedNiches = state.niches.filter(n => !initialNiches.includes(n));
    const removedNiches = initialNiches.filter((n: string) => !state.niches.includes(n));
    if (addedNiches.length > 0) onNicheAdd?.(state.niches, profile);
    if (removedNiches.length > 0) onNicheRemove?.(state.niches, profile);

    // Track keyword changes
    const addedKeywords = state.positiveKeywords.filter(k => !initialKeywords.includes(k));
    const removedKeywords = initialKeywords.filter((k: string) => !state.positiveKeywords.includes(k));
    if (addedKeywords.length > 0) onKeywordAdd?.(state.positiveKeywords, profile);
    if (removedKeywords.length > 0) onKeywordRemove?.(state.positiveKeywords, profile);

    // Track country changes
    const addedCountries = state.countries.filter(c => !initialCountries.includes(c));
    const removedCountries = initialCountries.filter((c: string) => !state.countries.includes(c));
    addedCountries.forEach(c => onCountryAdd?.(c, profile));
    removedCountries.forEach((c: string) => onCountryRemove?.(c, profile));

    // Track industry changes
    const addedIndustries = state.industries.filter(i => !initialIndustries.includes(i));
    const removedIndustries = initialIndustries.filter((i: string) => !state.industries.includes(i));
    addedIndustries.forEach(i => onIndustryAdd?.(i, profile));
    removedIndustries.forEach((i: string) => onIndustryRemove?.(i, profile));

    // Track filter apply events
    const jobFiltersChanged: string[] = [];
    const companyFiltersChanged: string[] = [];

    if (addedNiches.length > 0 || removedNiches.length > 0) jobFiltersChanged.push("niches");
    if (addedKeywords.length > 0 || removedKeywords.length > 0) jobFiltersChanged.push("keywords");
    if (addedCountries.length > 0 || removedCountries.length > 0) jobFiltersChanged.push("countries");
    if (JSON.stringify(state.workModes.sort()) !== JSON.stringify((prefs.workModes || []).sort())) jobFiltersChanged.push("workModes");
    if (JSON.stringify(state.jobTypes.sort()) !== JSON.stringify((prefs.jobTypes || []).sort())) jobFiltersChanged.push("jobTypes");
    if (state.minSalary !== prefs.minSalary) jobFiltersChanged.push("salary");
    if (JSON.stringify(state.educationLevels.sort()) !== JSON.stringify((prefs.educationLevels || []).sort())) jobFiltersChanged.push("education");

    if (addedIndustries.length > 0 || removedIndustries.length > 0) companyFiltersChanged.push("industries");
    if (JSON.stringify(state.companies.sort()) !== JSON.stringify((prefs.companies || []).sort())) companyFiltersChanged.push("companies");

    if (jobFiltersChanged.length > 0) {
      onFilterJobsApply?.(jobFiltersChanged, filtersAppliedCount, profile);
    }
    if (companyFiltersChanged.length > 0) {
      onFilterCompanyApply?.(companyFiltersChanged, filtersAppliedCount, profile);
    }

    // Normalize values before saving to ensure API compatibility
    updateSettingsMutation?.mutate(
      {
        jobFeedPreferences: {
          minSalary: state.minSalary,
          currencies: state.currencies,
          countries: state.countries.map(normalizeCountryCode),
          positiveKeywords: state.positiveKeywords,
          negativeKeywords: state.negativeKeywords,
          workModes: state.workModes.map(normalizeWorkMode),
          jobTypes: state.jobTypes.map(normalizeJobType),
          educationLevels: state.educationLevels,
          experienceLevels: state.experienceLevels,
          categories: state.niches, // Store niche categories in jobFeedPreferences
          companies: state.companies, // Store company filter from employer tab
          industries: state.industries, // Store industry filter from employer tab
        },
        // Keep for backward compatibility but prefer categories in jobFeedPreferences
        nicheCategories: state.niches,
      },
      shouldShowWebSaveToast
        ? {
          onSuccess: () => {
            toast.success("Settings saved successfully");
          },
        }
        : undefined,
    );
  }, [updateSettingsMutation, normalizeCountryCode, normalizeWorkMode, normalizeJobType, profile, onNicheAdd, onNicheRemove, onKeywordAdd, onKeywordRemove, onCountryAdd, onCountryRemove, onIndustryAdd, onIndustryRemove, onFilterJobsApply, onFilterCompanyApply, shouldShowWebSaveToast]);

  return (
    <SettingsBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      labels={labels}
      initialData={initialSettings}
      onSave={handleSaveSettings}
      onFiltersChange={handleFiltersChange}
      onCompanySearchChange={setCompanySearchQuery}
      availableOptions={availableOptions}
      estimatedJobCount={jobCountData?.count}
      isLoadingJobCount={isLoadingJobCount}
      presentation={presentation}
      drawerClassName={drawerClassName}
      sheetClassName={sheetClassName}
      sheetOverlayClassName={sheetOverlayClassName}
      hideHeader={hideHeader}
    />
  );
}
